import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  MessageSquare, Users, Send, Paperclip, Loader2, Search, ArrowLeft,
  Globe, User as UserIcon, FileText, Plus, Trash2, Hash,
} from 'lucide-react';

export default function Chat() {
  const [params] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeChan, setActiveChan] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [newMsg, setNewMsg] = useState('');
  const [posting, setPosting] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [me, setMe] = useState(null);
  const [showMobileList, setShowMobileList] = useState(true);

  const fileRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    document.title = 'Discussions - Reno Resolve';
    (async () => {
      try {
        const r = await api.get('/auth/me');
        setMe(r.data?.user || r.data);
      } catch (_) {}
    })();
    loadChannels();
    loadUsers();
  }, []);

  // Auto-open channel from URL params (?type=entity&entityType=chantier&entityId=xxx)
  useEffect(() => {
    const t = params.get('type');
    const et = params.get('entityType');
    const ei = params.get('entityId');
    const cid = params.get('channelId');
    if (cid) {
      setActiveId(cid);
      setShowMobileList(false);
      return;
    }
    if (t === 'entity' && et && ei) {
      (async () => {
        try {
          const r = await api.get(`/chat/entity/${et}/${ei}`);
          setActiveId(r.data.channel_id);
          setShowMobileList(false);
          loadChannels();
        } catch (e) {
          toast.error("Impossible d'ouvrir le canal contextuel");
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!activeId) { setMessages([]); setActiveChan(null); return; }
    loadMessages();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line
  }, [activeId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadChannels = async () => {
    try {
      const r = await api.get('/chat/channels');
      setChannels(r.data?.channels || []);
    } catch (_) {}
    setLoadingChannels(false);
  };
  const loadUsers = async () => {
    try {
      const r = await api.get('/users/list-slim');
      setUsers(r.data?.users || []);
    } catch (_) {}
  };
  const loadMessages = async () => {
    if (!activeId) return;
    setLoadingMsgs(true);
    try {
      const r = await api.get(`/chat/channels/${activeId}/messages`, { params: { limit: 100 } });
      setActiveChan(r.data?.channel);
      setMessages(r.data?.messages || []);
      // mark as read
      api.post(`/chat/channels/${activeId}/read`).catch(() => {});
      loadChannels();
    } catch (e) {
      toast.error("Impossible de charger les messages");
    }
    setLoadingMsgs(false);
  };

  const openDM = async (otherUserId) => {
    try {
      const r = await api.post('/chat/channels/direct', { other_user_id: otherUserId });
      setActiveId(r.data.channel_id);
      setShowMobileList(false);
      setShowSearch(false);
      setSearch('');
      loadChannels();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le DM");
    }
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeId) return;
    setPosting(true);
    try {
      const r = await api.post(`/chat/channels/${activeId}/messages`, { body: newMsg.trim() });
      setMessages((m) => [...m, r.data]);
      setNewMsg('');
      loadChannels();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    }
    setPosting(false);
  };

  const uploadFile = async (msgId, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append('file', f);
      await api.post(`/chat/messages/${msgId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      loadMessages();
      toast.success('Fichier ajouté');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur upload');
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSendAndUpload = async () => {
    if (!newMsg.trim()) {
      fileRef.current?.click();
      return;
    }
    await sendMessage();
  };

  const onFilePickedAfterSend = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPosting(true);
    try {
      // Crée d'abord un message si le textarea contient du texte, sinon un message simple "[fichier]"
      const body = newMsg.trim() || `📎 ${f.name}`;
      const r = await api.post(`/chat/channels/${activeId}/messages`, { body });
      const msgId = r.data?.message_id;
      const fd = new FormData();
      fd.append('file', f);
      await api.post(`/chat/messages/${msgId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setNewMsg('');
      loadMessages();
      loadChannels();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur upload');
    }
    if (fileRef.current) fileRef.current.value = '';
    setPosting(false);
  };

  const deleteMsg = async (msgId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await api.delete(`/chat/messages/${msgId}`);
      loadMessages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const fullUrl = (u) => {
    if (!u) return '#';
    return u.startsWith('http') ? u : (process.env.REACT_APP_BACKEND_URL || '') + u;
  };

  const channelIcon = (c) => {
    if (c.type === 'global') return <Globe className="w-4 h-4 text-blue-600 shrink-0" />;
    if (c.type === 'direct') return <UserIcon className="w-4 h-4 text-emerald-600 shrink-0" />;
    return <Hash className="w-4 h-4 text-purple-600 shrink-0" />;
  };

  const otherUsersFiltered = users
    .filter((u) => me ? u.user_id !== me.user_id : true)
    .filter((u) => !search || (u.name || u.email || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="-m-4 md:-m-6 h-[calc(100vh-3.5rem)] md:h-[calc(100vh-3.5rem)] flex bg-slate-50" data-testid="chat-page">
      {/* Sidebar list — toujours visible sur desktop, slide sur mobile */}
      <aside
        className={`${
          showMobileList ? 'flex' : 'hidden'
        } md:flex flex-col w-full md:w-72 lg:w-80 bg-white border-r border-slate-200 shrink-0`}
        data-testid="chat-channel-list"
      >
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900">Discussions</h2>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => setShowSearch((v) => !v)}
              data-testid="chat-new-dm-btn"
              title="Nouvelle conversation"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {showSearch && (
            <div className="space-y-2" data-testid="chat-user-picker">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un collègue…"
                  className="pl-9 h-9"
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {otherUsersFiltered.map((u) => (
                  <button
                    key={u.user_id}
                    type="button"
                    onClick={() => openDM(u.user_id)}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-sm text-slate-700 flex items-center gap-2"
                    data-testid={`chat-dm-user-${u.user_id}`}
                  >
                    <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                    <span className="truncate">{u.name || u.email}</span>
                  </button>
                ))}
                {otherUsersFiltered.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-2">Aucun collègue.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto" data-testid="chat-channels">
          {loadingChannels ? (
            <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
          ) : channels.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-400">Aucune discussion.</p>
          ) : (
            channels.map((c) => (
              <button
                key={c.channel_id}
                type="button"
                onClick={() => { setActiveId(c.channel_id); setShowMobileList(false); }}
                className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 flex items-start gap-3 ${
                  activeId === c.channel_id ? 'bg-blue-50 border-l-2 border-l-blue-600' : ''
                }`}
                data-testid={`chat-channel-${c.channel_id}`}
              >
                {channelIcon(c)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-900 truncate">{c.name}</span>
                    {c.unread > 0 && (
                      <Badge className="bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1.5">
                        {c.unread > 99 ? '99+' : c.unread}
                      </Badge>
                    )}
                  </div>
                  {c.last_message_at && (
                    <p className="text-[11px] text-slate-400 truncate">
                      {String(c.last_message_at || '').slice(0, 16).replace('T', ' ')}
                      {c.last_message_by ? ` · ${c.last_message_by}` : ''}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      {/* Main thread */}
      <section
        className={`${showMobileList ? 'hidden' : 'flex'} md:flex flex-col flex-1 min-w-0`}
        data-testid="chat-main"
      >
        {!activeId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center">
            <MessageSquare className="w-16 h-16 mb-3 text-slate-200" />
            <p className="text-sm">Sélectionnez une discussion pour commencer</p>
            <p className="text-xs mt-1">Cliquez sur <Plus className="w-3 h-3 inline" /> pour démarrer un DM avec un collègue</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setShowMobileList(true)}
                className="md:hidden p-1 -ml-1 rounded hover:bg-slate-100"
                data-testid="chat-back-btn"
              >
                <ArrowLeft className="w-4 h-4 text-slate-600" />
              </button>
              {activeChan && channelIcon(activeChan)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {activeChan?.name || 'Discussion'}
                </p>
                {activeChan?.entity_label && (
                  <p className="text-[11px] text-slate-400 truncate">
                    Lié à : {activeChan.entity_type} · {activeChan.entity_label}
                  </p>
                )}
              </div>
            </header>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-messages">
              {loadingMsgs && messages.length === 0 && (
                <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
              )}
              {messages.map((m) => {
                const mine = me && m.author?.user_id === me.user_id;
                return (
                  <div
                    key={m.message_id}
                    className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                    data-testid={`chat-msg-${m.message_id}`}
                  >
                    <div className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-3 py-2 ${
                      mine
                        ? 'bg-blue-600 text-white rounded-br-sm'
                        : 'bg-white border border-slate-200 text-slate-900 rounded-bl-sm'
                    }`}>
                      {!mine && (
                        <p className={`text-[11px] font-semibold mb-0.5 ${mine ? 'text-blue-100' : 'text-blue-700'}`}>
                          {m.author?.name || m.author?.email}
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap leading-snug">{m.body}</p>
                      {m.attachments && m.attachments.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {m.attachments.map((a, i) => (
                            <a
                              key={i}
                              href={fullUrl(a.url)}
                              target="_blank" rel="noreferrer"
                              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                                mine ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              <FileText className="w-3 h-3" /> {a.filename}
                            </a>
                          ))}
                        </div>
                      )}
                      <div className={`flex items-center justify-between gap-2 mt-1 text-[10px] ${
                        mine ? 'text-blue-100' : 'text-slate-400'
                      }`}>
                        <span>{String(m.created_at || '').slice(11, 16)}</span>
                        {mine && (
                          <button
                            type="button" onClick={() => deleteMsg(m.message_id)}
                            className="opacity-0 hover:opacity-100 transition"
                            data-testid={`msg-delete-${m.message_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>

            {/* Composer */}
            <div className="p-3 border-t border-slate-200 bg-white shrink-0">
              <div className="flex items-end gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
                  className="hidden"
                  onChange={onFilePickedAfterSend}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={posting}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
                  data-testid="chat-attach-btn"
                  title="Joindre un fichier"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <textarea
                  value={newMsg}
                  onChange={(e) => setNewMsg(e.target.value)}
                  placeholder="Écrire un message…"
                  rows={1}
                  className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none max-h-32"
                  data-testid="chat-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <Button
                  onClick={sendMessage}
                  disabled={posting || !newMsg.trim()}
                  className="bg-blue-600 hover:bg-blue-700 shrink-0"
                  data-testid="chat-send-btn"
                >
                  {posting
                    ? <Loader2 key="s-load" className="w-4 h-4 animate-spin" />
                    : <Send key="s-icon" className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
